import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Heart } from "lucide-react";
import { IMAGES, EVENT, DONATION_URL } from "@/lib/siteData";
import { StiftungChance } from "@/components/site/StiftungChance";

const ease = [0.76, 0, 0.24, 1];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.11, delayChildren: 0.45 } },
};

const lineVariant = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.95, ease } },
};

const Line = ({ children }) => (
  <span className="reveal-mask">
    <motion.span variants={lineVariant} className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bearY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const bearScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      {/* Parallax bear backdrop */}
      <motion.div style={{ y: bearY, scale: bearScale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.heroTeddy}
          alt=""
          className="h-full w-full object-cover object-[70%_center] opacity-40 sm:opacity-50"
        />
      </motion.div>

      {/* Spotlight glows */}
      <div className="pointer-events-none absolute -top-24 right-[-10%] z-0 h-[520px] w-[520px] rounded-full bg-magenta/25 blur-[130px]" />
      <div className="pointer-events-none absolute bottom-[-15%] left-[-10%] z-0 h-[520px] w-[520px] rounded-full bg-cyan/20 blur-[130px]" />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-ink/70 via-ink/40 to-ink" />

      <motion.div style={{ y: textY }} className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-4 sm:px-6 pt-28 pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="mb-6 flex items-center gap-3 text-xs sm:text-sm uppercase tracking-[0.35em] text-cyan"
          data-testid="hero-kicker"
        >
          <Heart size={14} className="text-magenta" /> {EVENT.kicker}
        </motion.p>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          className="font-display tracking-tight text-ivory text-5xl sm:text-7xl lg:text-[8.5vw] leading-[1.04]"
        >
          <Line>Wenn Kinder</Line>
          <Line>kämpfen müssen,</Line>
          <Line>
            <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-cyan via-violet to-magenta">
              dürfen sie nicht
            </span>
          </Line>
          <Line>allein sein.</Line>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 1, ease }}
          className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between"
        >
          <p className="max-w-md text-base sm:text-lg text-ivory/70 leading-relaxed">
            Ein Benefizabend von {EVENT.host} zugunsten der <StiftungChance label={EVENT.beneficiary} testid="hero-stiftung-link" />. Gemeinsam für kranke Kinder.
          </p>

          <div className="flex flex-wrap items-center gap-4">
            <motion.a
              whileTap={{ scale: 0.97 }}
              href={DONATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-magenta px-8 py-4 text-sm font-medium text-white"
              data-testid="hero-donate-button"
            >
              <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-violet to-cyan transition-transform duration-500 group-hover:translate-x-0" />
              <Heart size={16} className="relative z-10" />
              <span className="relative z-10">Jetzt spenden</span>
            </motion.a>
            <motion.a
              whileTap={{ scale: 0.97 }}
              href="#anmelden"
              onClick={(e) => { e.preventDefault(); document.querySelector("#anmelden")?.scrollIntoView({ behavior: "smooth" }); }}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 text-sm font-medium text-ivory transition-colors duration-300 hover:border-white/50"
              data-testid="hero-register-button"
            >
              Zum Anlass anmelden
            </motion.a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.1, duration: 1 }}
          className="mt-16 flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-ivory/40"
        >
          <ArrowDown size={14} className="animate-bounce" /> Nach unten scrollen
        </motion.div>
      </motion.div>
    </section>
  );
};
